import React, { useEffect, useState } from 'react';
import { Form, Input, notification } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { Button } from '../ui/button';
import { api } from '../../api';

const FormWrapper = styled.div`
  width: 400px;
  margin: auto;
  margin-top: 50px;
  background-color: #fff;
  padding: 20px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  h2,
  h5 {
    text-align: center;
    margin: 0px 0px 8px 0px;
  }
  h5 {
    margin: 0px 0px 2em 0px;
  }
`;

interface IForm {
  email: string;
  password: string;
}

export const ResetPassword: React.FC = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [notifApi, contextHolder] = notification.useNotification();

  useEffect(() => {
    if (!token) {
      navigate('/');
    } else {
      api.auth.verifyToken(token).catch((error) => {
        console.error(error);
        navigate('/');
      });
    }
  }, [token, navigate]);

  const resetPasswordSuccessNotification = () => {
    notifApi.success({
      message: 'Password Reset Successful',
      description: 'You have successfully reset your password',
      placement: 'topRight',
      onClose: () => {
        navigate('/login');
      },
      duration: 1,
    });
  };
  const resetPasswordFailNotification = (message: string) => {
    notifApi.error({
      message: `Password Reset Failed`,
      description: `An error occurred: ${message}`,
      placement: 'topRight',
      onClose: () => {},
      duration: 1,
    });
  };

  const onSubmit = (values: IForm) => {
    const { password } = values;
    if (!token || !password) {
      return;
    }

    setLoading(true);

    api.auth
      .updatePassword(token, password)
      .then(() => {
        resetPasswordSuccessNotification();
      })
      .catch((error) => {
        console.error(error);
        resetPasswordFailNotification(
          error.response?.data?.message ?? 'Please try again',
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      {contextHolder}

      <FormWrapper>
        <h2>Reset password</h2>
        <Form
          name="form-reset-pwd"
          onFinish={onSubmit}
          initialValues={{ remember: true }}
          scrollToFirstError
          disabled={loading}
        >
          <Form.Item
            name="password"
            rules={[
              { required: true, message: 'Please input your new password!' },
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="site-form-item-icon" />}
              type="password"
              placeholder="New Password"
            />
          </Form.Item>

          <Form.Item
            name="confirm"
            dependencies={['password']}
            hasFeedback
            rules={[
              {
                required: true,
                message: 'Please confirm your new password!',
              },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue('password') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    'The two passwords that you entered do not match!',
                  );
                },
              }),
            ]}
          >
            <Input.Password
              prefix={<LockOutlined className="site-form-item-icon" />}
              type="password"
              placeholder="Confirm New Password"
            />
          </Form.Item>

          <Form.Item>
            <Button type="submit" variant="default" className="w-full">
              Reset Password
            </Button>
          </Form.Item>
        </Form>
      </FormWrapper>
    </>
  );
};
